import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { useLocationStore } from "../../../store/useLocationStore";
import { useDeviceStore } from "../../../store/useDeviceStore";
import { useGetAlertHistories } from "../../../queries/alerts";
import { useGetMultiDeviceParameters } from "../../../queries/parameters";
import { convertDate } from "../../../utils/string";
import { useIsAdmin } from "../../../utils/auth";
import { Icon } from "@iconify/react";
import DaysSelector from "../../../components/DaysSelector";

export interface AlertHistory {
  id: string;
  timestamp: string;
  location: string;
  alertType: string;
  message: string;
  deviceId: string;
  deviceName: string;
}

export default function AlertFeed() {
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const { selectedLocationId } = useLocationStore();
  const { selectedDeviceId, selectedDeviceIds, setSelectedDevice } =
    useDeviceStore();
  const [selectedDays, setSelectedDays] = useState<number | null>(30);

  const fromDate = selectedDays
    ? new Date(Date.now() - selectedDays * 86400000).toISOString().split("T")[0]
    : "";
  const toDate = selectedDays
    ? new Date().toISOString().split("T")[0]
    : "";
  const { data: _alertHistories } = useGetAlertHistories(
    selectedLocationId,
    fromDate,
    toDate,
  );
  // Check parameters across all selected devices so the feed matches the
  // page-level "New Alert" gating (alerts can target any selected device).
  const paramFetchIds =
    selectedDeviceIds.length > 0
      ? selectedDeviceIds
      : selectedDeviceId
        ? [selectedDeviceId]
        : [];
  const { data: deviceParameters } = useGetMultiDeviceParameters(paramFetchIds);
  const alertHistories = _alertHistories || [];
  const hasParameters = deviceParameters && deviceParameters.length > 0;

  // Show message if no parameters are configured for the device. Read-only
  // USER role can't add parameters, so show a small "contact admin" note
  // instead of the actionable empty state shown to admins.
  if (!hasParameters) {
    if (!isAdmin) {
      return (
        <div className="flex-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
          <div className="p-12 flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Icon icon="mdi:information-outline" className="w-4 h-4" />
            <span>
              No parameters configured for this device. Contact your admin to
              set them up.
            </span>
          </div>
        </div>
      );
    }
    return (
      <div className="flex-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        <div className="p-24 text-center">
          <div className="w-24 h-24 mx-auto mb-6 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
            <Icon
              icon="mdi:alert-circle-outline"
              className="w-12 h-12 text-amber-600 dark:text-amber-400"
            />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
            Add Parameter for the device first to set the Alert
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
            You need to configure parameters for your device before you can
            create alerts. Parameters define what data your device will monitor
            and send alerts for.
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Icon icon="mdi:information-outline" className="w-4 h-4" />
            <span>Go to Device Settings to add parameters</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
      {/* Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
            Recent Alerts
          </span>

          {/* Duration Selector */}
          <DaysSelector
            options={[7, 14, 30, 60, 90].map((days) => ({
              value: days,
              label: `${days}d`,
            }))}
            selected={selectedDays}
            onSelect={(days) =>
              setSelectedDays(selectedDays === days ? null : days)
            }
          />
        </div>
      </div>

      {/* Alert List */}
      <div className="flex-1">
        {alertHistories.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-24 h-24 mx-auto mb-4 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
              <Icon icon="mdi:bell-off" className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No alerts shown
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              There are no alerts available at this time.
            </p>
          </div>
        ) : (
          alertHistories.map((alert) => (
            <div
              key={alert.id}
              className="p-6 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-start">
                {/* Alert Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {convertDate(alert.timestamp)}
                    </span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {alert.location}
                    </span>
                    <Badge className="bg-teal-100 text-teal-800 hover:bg-teal-100 dark:bg-teal-900/30 dark:text-teal-400 text-xs font-medium">
                      {alert.deviceName}
                    </Badge>
                    <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400 text-xs font-medium">
                      {alert.alertType}
                    </Badge>
                  </div>

                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-3">
                    {alert.message}
                  </p>

                  <Button
                    variant="link"
                    size="sm"
                    className="mt-2"
                    onClick={() => {
                      setSelectedDevice(alert.deviceId, alert.deviceName);
                      navigate("/dashboard");
                    }}
                  >
                    View device
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
